import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import Lottie from 'lottie-react';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Skeleton from '@material-ui/lab/Skeleton';

import {
    isLotteryHasAnimation,
    lotteryInstantTypes,
    lotteryParticipantTypes,
    lotteryAdminTypes
} from 'config/promoLotteryTypes';
import { gameTypes } from 'config/promoGameTypes';
import { feverZoomScale } from 'styles/keyframes';
import { generateMuiTextFieldCustomColorStyle } from 'client/fever_form/style';
import animationDataCorrect from 'editor/game_checkIn/components/lotteryTaskCarousel/lottieFiles/correct.json';
import animationDataNotCorrect from 'editor/game_checkIn/components/lotteryTaskCarousel/lottieFiles/not-correct.json';
import animationDataLock from 'editor/game_checkIn/components/lotteryTaskCarousel/lottieFiles/lock.json';

import ButtonWithLoadingEffect from 'Mui/ButtonWithLoadingEffect';
import BtnImgUpload from 'editor/game_checkIn/components/lotteryTaskCarousel/BtnImgUpload';
import ResponsiveBgImage from 'client/components/ResponsiveBgImage';
import RatioContainerStyle from 'components/RatioContainerStyle';
import { btnVariants } from 'styles/common';

export default function LotteryTaskCard({
    gameType,
    type,
    status,
    title,
    imgSrc,
    checkInTargetQuantity,
    prizeLeftQuantity,
    prizeTotalQuantity,
    componentStyle = {},
    isEditorMode,
    isIniting,
    isLazyLoading,
    onImgDrop,
    onImgDropError,
    onLazyLoaded = () => {},
    onTitleChange = () => {},
    onButtonClick = async () => {},
    firstView = false,
    isMgmSelf
}) {
    const showPlaceholder = isIniting || isLazyLoading;
    const { t } = useTranslation();
    // [#86eqmcjh6] 在關卡獎上 24px 是文字 "完成 1 關可解鎖額外獎勵"，當畫面滑到這段文字時， load image 跟 task prize 內容
    const { ref, inView } = useInView({
        triggerOnce: true,
        rootMargin: '24px 0%',
        skip: !isLazyLoading
    });
    const lottieRef = useRef(null);
    const [showAnimation, setShowAnimation] = useState(true);
    const [processingOnClick, setProcessingOnClick] = useState(false);
    const [titleType, setTitleType] = useState(titleTypes.TEXT);
    [title, imgSrc] = (() => {
        const defaultTitleAndImg = lotteryDefaultTitleAndImg[type] || { getTitle: () => '' };
        return [title ?? defaultTitleAndImg.getTitle(t), imgSrc ?? defaultTitleAndImg.imgSrc];
    })();
    const { borderRadius, color, backgroundColor } = componentStyle;
    const lottieData = getStatusMappingLottieData(status);
    const hintText = getHintText({
        gameType,
        t,
        checkInTargetQuantity,
        prizeLeftQuantity,
        prizeTotalQuantity,
        isMgmSelf
    });
    const btnText = getBtnText({ t, type, status });
    const [btnVariant, btnCustomStyle] = getBtnVariantAndStyle({
        type,
        status,
        styleObj: componentStyle
    });
    const eventHandlers = isEditorMode
        ? {
              onMouseEnter: () => setTitleType(titleTypes.INPUT),
              onMouseLeave: () => setTitleType(titleTypes.TEXT)
          }
        : {};

    useEffect(() => {
        if (lottieRef.current) {
            if (isIniting || isLazyLoading || [statuses.LOCKED].includes(status)) {
                lottieRef.current.goToAndStop(0);
            } else {
                lottieRef.current.play();
            }
        }
    }, [status, isIniting, isLazyLoading]);
    useEffect(() => {
        if (inView) {
            onLazyLoaded();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView]);
    useEffect(() => {
        if (status === statuses.UNLOCKED) {
            setShowAnimation(true);
        } else {
            setShowAnimation(false);
        }
    }, [status]);
    async function handleClick() {
        if (status === statuses.UNLOCKED) {
            setProcessingOnClick(true);
            await onButtonClick();
            setProcessingOnClick(false);
        }
    }

    return (
        <div
            ref={ref}
            css={[containerStyle(firstView), css(`border-radius: ${borderRadius}px;`)]}
            {...eventHandlers}
        >
            <RatioContainerStyle ratio={100 / 67} enableOuterContainer={false}>
                {(isEditorMode || firstView) && (
                    <BtnImgUpload
                        onDropSuccess={onImgDrop}
                        onDropError={onImgDropError}
                        firstView={firstView}
                    />
                )}
                {showPlaceholder ? (
                    <Skeleton
                        animation="wave"
                        css={css(
                            `width: 100%; height: 100%; transform: scale(1); border-radius: ${borderRadius}px ${borderRadius}px 0 0;`
                        )}
                    />
                ) : (
                    <ResponsiveBgImage
                        style={`border-radius: ${borderRadius}px ${borderRadius}px 0 0;`}
                        src={imgSrc}
                    />
                )}
                <div
                    css={maskStyle({
                        borderRadius,
                        backgroundColor,
                        show: !showPlaceholder && status === statuses.PRIZE_EMPTY
                    })}
                />
            </RatioContainerStyle>
            <div className="ratio-scale-wrapper" css={css(`padding-top: 180px;`)}>
                <div className="icon-wrapper" css={css(`background-color: ${backgroundColor}`)}>
                    <Lottie
                        lottieRef={lottieRef}
                        animationData={lottieData}
                        loop={false}
                        autoPlay={false}
                    />
                </div>
                <div
                    className="content-wrapper"
                    css={getContentWrapperStyle(color, backgroundColor, borderRadius)}
                >
                    <div className="content-wrapper-text">
                        <Typography css={checkQuantityTextStyle} variant="subtitle2">
                            {showPlaceholder ? (
                                <Skeleton
                                    animation="wave"
                                    css={[textSkeletonStyle(color), css(`width: 100px;`)]}
                                />
                            ) : (
                                hintText
                            )}
                        </Typography>
                        {titleType === titleTypes.INPUT && (
                            <TextField
                                value={title}
                                onChange={e => onTitleChange(e.target.value)}
                                onMouseMove={e => e.stopPropagation()}
                                multiline
                                fullWidth
                                maxRows={2}
                                css={[titleInputStyle, generateMuiTextFieldCustomColorStyle(color)]}
                            />
                        )}
                        {titleType === titleTypes.TEXT && (
                            <Typography variant="h6" css={titleTextStyle(firstView)}>
                                {showPlaceholder ? (
                                    <Skeleton
                                        animation="wave"
                                        css={[textSkeletonStyle(color), css(`width: 120px;`)]}
                                    />
                                ) : (
                                    title
                                )}
                            </Typography>
                        )}
                    </div>
                    <ButtonWithLoadingEffect
                        disableAllRipple
                        color="primary"
                        variant={btnVariant}
                        css={[btnStyle, btnCustomStyle, showAnimation && animateStyle]}
                        onClick={handleClick}
                        isLoading={processingOnClick}
                    >
                        {showPlaceholder ? (
                            <Skeleton
                                animation="wave"
                                css={[textSkeletonStyle(color), css(`width: 120px;`)]}
                            />
                        ) : (
                            btnText
                        )}
                    </ButtonWithLoadingEffect>
                </div>
            </div>
        </div>
    );
}

const titleTypes = {
    INPUT: 'input',
    TEXT: 'TEXT'
};

export const statuses = {
    LOCKED: 'LOCKED',
    UNLOCKED: 'UNLOCKED',
    UNLOCKED_ADMIN_PRIZE: 'UNLOCKED_ADMIN_PRIZE',
    PRIZE_EMPTY: 'PRIZE_EMPTY',
    IS_FULFILL: 'IS_FULFILL',
    IS_UN_QUALIFY: 'IS_UN_QUALIFY'
};

function getStatusMappingLottieData(status) {
    switch (status) {
        case statuses.IS_FULFILL:
        case statuses.UNLOCKED_ADMIN_PRIZE:
            return animationDataCorrect;
        case statuses.IS_UN_QUALIFY:
        case statuses.PRIZE_EMPTY:
            return animationDataNotCorrect;
        default:
            return animationDataLock;
    }
}

const lotteryDefaultTitleAndImg = {
    [lotteryAdminTypes.ADMIN]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.admin_draw'),
        imgSrc: 'https://assets.fevercdn.com/promotion/img/check_in/task_card_img/admin.png'
    },
    [lotteryInstantTypes.JOIN_AND_GET]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.join'),
        imgSrc: 'https://assets.fevercdn.com/promotion/img/check_in/task_card_img/join_and_get.png'
    },
    [lotteryParticipantTypes.WIN_WHEEL]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.win_wheel'),
        imgSrc: 'https://assets.fevercdn.com/nc-admin/images/promo_module_winwheel.png'
    },
    [lotteryParticipantTypes.SCRATCH_CARD]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.scratch_card'),
        imgSrc: 'https://assets.fevercdn.com/nc-admin/images/promo_module_scratch_card.png'
    },
    [lotteryParticipantTypes.POKE_GAME]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.poke'),
        imgSrc: 'https://assets.fevercdn.com/nc-admin/images/promo_module_poke_game.png'
    },
    [lotteryParticipantTypes.RED_ENVELOPE_RAIN]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.envelope_rain'),
        imgSrc: 'https://assets.fevercdn.com/nc-admin/images/promo_module_red_envelope_rain.png'
    },
    [lotteryParticipantTypes.SLOTS]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.slots'),
        imgSrc: 'https://assets.fevercdn.com/nc-admin/images/promo_module_slots.png'
    },
    [lotteryParticipantTypes.PICK_THE_PRIZE]: {
        getTitle: t => t('label.check_in.lottery_task_card.title.pick_the_prize'),
        imgSrc: 'https://assets.fevercdn.com/nc-admin/images/promo_module_choose.png'
    }
};

function getHintText({
    gameType,
    t,
    checkInTargetQuantity,
    prizeLeftQuantity,
    prizeTotalQuantity,
    isMgmSelf
}) {
    let text = (() => {
        switch (gameType) {
            case gameTypes.MULTI_CHALLENGE:
            case gameTypes.CHALLENGE_PHOTO_FRAME_COMPOSITE:
                return t('label.challenge.lottery_task_card.finish_count', {
                    count: checkInTargetQuantity
                });
            case gameTypes.MGM_REFERRAL:
                return t('label.mgm_share.lottery_task_card.mgm_share_count', {
                    count: checkInTargetQuantity
                });
            case gameTypes.MGM_SHARE:
                return t('label.mgm_share.lottery_task_card.mgm_share_count', {
                    count: checkInTargetQuantity
                });
            case gameTypes.CHECK_IN:
            default:
                return t('label.check_in.lottery_task_card.check_in_count', {
                    count: checkInTargetQuantity
                });
        }
    })();
    if (isMgmSelf) {
        text = t('label.mgm_share.lottery_task_card.mgm_share_count', {
            count: checkInTargetQuantity
        });
    }
    if (prizeLeftQuantity !== undefined) {
        text += `・${t('label.check_in.lottery_task_card.prize_left', {
            count: prizeLeftQuantity
        })}`;
    }
    if (prizeTotalQuantity !== undefined) {
        text += `・${t('label.check_in.lottery_task_card.prize_total', {
            count: prizeTotalQuantity
        })}`;
    }
    return text;
}
function getBtnText({ t, type, status }) {
    switch (status) {
        case statuses.IS_FULFILL:
            return t('label.check_in.lottery_task_card.fulfill');
        case statuses.IS_UN_QUALIFY:
            return t('label.lottery_task_card.unqualify');
        case statuses.UNLOCKED_ADMIN_PRIZE:
            return t('label.check_in.lottery_task_card.unlocked.admin_prize');
        case statuses.PRIZE_EMPTY:
            return t('label.check_in.lottery_task_card.prize_empty');
        case statuses.UNLOCKED: {
            if (type === lotteryParticipantTypes.PICK_THE_PRIZE) {
                return t('btn.open_winner.client_pick');
            } else if (isLotteryHasAnimation(type)) {
                return t('label.check_in.lottery_task_card.unlocked.participant_prize.luck');
            } else {
                return t('label.check_in.lottery_task_card.unlocked.participant_prize');
            }
        }
        case statuses.LOCKED:
        default:
            return t('label.check_in.lottery_task_card.locked');
    }
}

function getBtnVariantAndStyle({
    type,
    status,
    styleObj: { borderRadius, color, backgroundColor }
}) {
    if (type !== lotteryAdminTypes.ADMIN && status === statuses.UNLOCKED) {
        return [
            btnVariants.OUTLINED,
            css(
                `font-size:14px; background-color: ${color}; color: ${backgroundColor}; border-radius: ${borderRadius}px; &:hover, &:active {
                    background-color: ${color}; color: ${backgroundColor}; border-radius: ${borderRadius}px;
                }`
            )
        ];
    } else {
        return [btnVariants.TEXT, css(`font-size:14px; color: ${color}; cursor: not-allowed;`)];
    }
}

const containerStyle = firstView => css`
    .ratio-scale-wrapper {
        position: relative;
        > div {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            right: 0;
        }
    }
    .content-wrapper {
        padding: 16px;
        padding-top: 30px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: stretch;
        position: relative;
        > .content-wrapper-text {
            display: flex;
            flex-direction: column;
            align-items: stretch;
        }
    }
    div.icon-wrapper {
        width: 60px;
        height: 60px;
        border-radius: 30px;
        z-index: 3;
        position: absolute;
        left: 50%;
        top: 0;
        transform: translate(-50%, -50%);
    }
    ${firstView &&
    `.btn-upload-img {
        opacity: 1;`}
    &:hover {
        .btn-upload-img {
            opacity: 1;
        }
    }
`;

const getContentWrapperStyle = (color, bgColor, borderRadius) => css`
    color: ${color};
    background-color: ${bgColor};
    border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
`;

const btnStyle = css`
    font-size: 20px;
    align-self: center;
    min-width: 160px;
    &:hover {
        background-color: transparent;
    }
`;

const animateStyle = css`
    animation: ${feverZoomScale} 3.5s ease-in-out;
`;

const titleInputStyle = css`
    & .MuiInputBase-root {
        padding: 0;
    }
    & textarea.MuiInputBase-input.MuiInput-input {
        text-align: center;
        font-size: 1.25rem;
        font-weight: 500;
        line-height: 1.334;
    }
`;

const checkQuantityTextStyle = css`
    color: inherit;
    text-align: center;
    margin-bottom: 4px;
`;

const titleTextStyle = firstView => css`
    display: block; /* Fallback for non-webkit */
    display: -webkit-box;
    white-space: pre-wrap;
    font-weight: 500;
    text-align: center;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    color: inherit;
    ${firstView &&
    `    border-bottom: 1px solid;
`}
`;

const textSkeletonStyle = color => css`
    display: inline-block;
    background-color: ${color};
`;

const maskStyle = ({ borderRadius, backgroundColor, show }) => css`
    border-radius: ${borderRadius}px ${borderRadius}px 0 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    background-color: ${backgroundColor};
    opacity: ${show ? '56%' : 0};
    transition: all 0.2s linear;
`;
